import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Link,
    MenuItem,
    Popover,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { generateUserWebGameSessionCode, getUserAggregatedInfo, getUserTags, getUsersPaginated, getWebGameConfigs, getWebGameImagePresets, getWebGameUploadedImages, createWebGameImagePreset, deleteWebGameImagePreset, getWebGameSoundAssets, updateWebGameConfig, uploadFile, DestinationFolder, normalizeCompetitionBackgroundUrl, resolveStrapiMediaUrlForDisplay, resolveWebGameSoundAssetUrl, normalizeWebGameUploadCollectionStoredPath, normalizeWebGameCoverImageStoredPath, normalizeWebGameUploadStoredPath, resolveWebGameUploadsPublicUrl, isWebGameUploadsApiPath } from '../Api';
import AccountMainInfoDto from '../models/dtos/AccountMainInfoDto';
import AggregateUserInfoDto from '../models/dtos/AggregateUserInfoDto';
import { UpdateWebGameAdminConfigRequest, WebGameAdminConfigDto, WebGameImagePresetDto } from '../models/dtos/WebGameDto';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import {
    formatWebGameSoundMomentDuration,
    mergeWebGameSfxCatalogFromEditor,
    webGameConfigurableSfxMomentKeys,
    webGameSoundProfileForGame,
    webGameSoundSupportSummary,
    WebGameSoundMomentDefinition,
    WebGameSoundUiLang
} from '../webGames/soundCatalog';
import { listStrapiWebGameFolderAudio, listStrapiWebGameFolderImages, StrapiFolderImage } from '../webGames/strapiImageFolder';
import { collectWebGameProjectImageUrls } from '../webGames/webGameProjectImageUrls';
import {
    SLOT_MACHINE_GAME_CODE,
    SLOT_MACHINE_SYMBOL_UPLOADS_FOLDER,
    SlotMachineSymbolImageEditor,
    slotMachineSymbolUploadStoredPath,
    slotSymbolImagesFromMetadata,
    mergeSlotSymbolImagesIntoMetadata
} from '../webGames/slotMachineSymbolImages';

type SegmentWinRuleEditor = {
    tagNames: string[];
    welcomeFavorableWinsTarget: string;
    welcomeMaxRoundsInWindow: string;
};

type EditorState = {
    name: string;
    description: string;
    enabled: boolean;
    order: number;
    coverImageUrl: string;
    htmlTemplateName: string;
    localizationsText: string;
    descriptionLocalizationsText: string;
    metadataText: string;
    playerWinChance: string;
    engagementTuningEnabled: boolean;
    engagementMaxBoost: string;
    engagementLossStreakStep: string;
    engagementMinPlayerWinChance: string;
    engagementMaxPlayerWinChance: string;
    welcomeFavorableWinsTarget: string;
    welcomeMaxRoundsInWindow: string;
    segmentRules: SegmentWinRuleEditor[];
    backgroundMusicPath: string;
    backgroundMusicVolume: string;
    backgroundMusicPlaybackRate: string;
    soundUploadPrefix: string;
    slotSymbolImages: SlotMachineSymbolImageEditor[];
    sfxPaths: Record<string, string>;
};

const DEFAULT_PLAYER_WIN_CHANCE = 0.45;
const DEFAULT_ENGAGEMENT_TUNING_ENABLED = false;
const DEFAULT_ENGAGEMENT_MAX_BOOST = 0.03;
const DEFAULT_ENGAGEMENT_LOSS_STREAK_STEP = 3;
const DEFAULT_ENGAGEMENT_MIN_PLAYER_WIN_CHANCE = 0.15;
const DEFAULT_ENGAGEMENT_MAX_PLAYER_WIN_CHANCE = 0.60;
const DEFAULT_WELCOME_FAVORABLE_WINS_TARGET = 0;
const DEFAULT_WELCOME_MAX_ROUNDS_IN_WINDOW = 0;
const DEFAULT_BACKGROUND_MUSIC_VOLUME = 0.4;
const DEFAULT_BACKGROUND_MUSIC_PLAYBACK_RATE = 1;
const AVIATOR_SIMPLIFIED_GAME_CODE = 'aviator-simplified';

const emptyEditor: EditorState = {
    name: '',
    description: '',
    enabled: false,
    order: 0,
    coverImageUrl: '',
    htmlTemplateName: '',
    localizationsText: '{}',
    descriptionLocalizationsText: '{}',
    metadataText: '{}',
    playerWinChance: String(DEFAULT_PLAYER_WIN_CHANCE),
    engagementTuningEnabled: DEFAULT_ENGAGEMENT_TUNING_ENABLED,
    engagementMaxBoost: String(DEFAULT_ENGAGEMENT_MAX_BOOST),
    engagementLossStreakStep: String(DEFAULT_ENGAGEMENT_LOSS_STREAK_STEP),
    engagementMinPlayerWinChance: String(DEFAULT_ENGAGEMENT_MIN_PLAYER_WIN_CHANCE),
    engagementMaxPlayerWinChance: String(DEFAULT_ENGAGEMENT_MAX_PLAYER_WIN_CHANCE),
    welcomeFavorableWinsTarget: String(DEFAULT_WELCOME_FAVORABLE_WINS_TARGET),
    welcomeMaxRoundsInWindow: String(DEFAULT_WELCOME_MAX_ROUNDS_IN_WINDOW),
    segmentRules: [],
    backgroundMusicPath: '',
    backgroundMusicVolume: String(DEFAULT_BACKGROUND_MUSIC_VOLUME),
    backgroundMusicPlaybackRate: String(DEFAULT_BACKGROUND_MUSIC_PLAYBACK_RATE),
    soundUploadPrefix: '',
    slotSymbolImages: [],
    sfxPaths: {}
};

const PLAY_AS_USER_STORAGE_KEY = 'webGames.playAsUserId';

type WebGamesUiLang = 'ro' | 'en';

function webGamesUiLangFromBrowser(): WebGamesUiLang {
    if (typeof navigator === 'undefined') return 'en';
    return navigator.language.toLowerCase().startsWith('ro') ? 'ro' : 'en';
}

const WEB_GAMES_ENGAGEMENT_UI: Record<WebGamesUiLang, {
    catalogEngagementHint: string;
    catalogEngagementBullets: string[];
    editPerGameAlert: string;
    cardWinChanceChip: string;
    cardEngagementOnChip: string;
    cardEngagementOffChip: string;
    cardOpeningConfiguredChip: string;
    cardOpeningDefaultChip: string;
    sectionTitle: string;
    sectionIntro: string;
    twoLayersIntro: string;
    storageNote: string;
    defaultsNote: string;
    aviatorWarning: string;
    metadataMergeNote: string;
    layerOpeningTitle: string;
    layerOpeningIntro: string;
    layerOpeningProgressNote: string;
    layerV1Title: string;
    layerV1Intro: string;
    playerWinChanceLabel: string;
    playerWinChanceHelper: string;
    engagementSwitchLabel: string;
    engagementSwitchCaption: string;
    engagementMaxBoostLabel: string;
    engagementMaxBoostHelper: string;
    engagementLossStreakLabel: string;
    engagementLossStreakHelper: string;
    engagementMinLabel: string;
    engagementMinHelper: string;
    engagementMaxLabel: string;
    engagementMaxHelper: string;
    welcomeTitle: string;
    welcomeIntro: string;
    welcomeFavorableWinsLabel: string;
    welcomeMaxRoundsLabel: string;
    welcomeHelper: string;
    segmentRulesTitle: string;
    segmentRulesIntro: string;
    segmentRuleTagNamesLabel: string;
    segmentRuleTagNamesHelper: string;
    segmentRuleWelcomeFavorableWinsLabel: string;
    segmentRuleWelcomeMaxRoundsLabel: string;
    defaultWinChanceTitle: string;
    defaultWinChanceIntro: string;
    advancedMetadataTitle: string;
    userTagsNote: string;
}> = {
    en: {
        catalogEngagementHint:
            'Win / engagement settings are edited per mini game: Games → Web Mini Games → Edit on a card. There is no global control; each game has its own metadata in MongoDB.',
        catalogEngagementBullets: [
            'Pick an Analytics user tag, then set how generous the games server is for that segment and for how many sessions (rounds).',
            'Everyone else uses the default normal win chance for this game.',
            'Rules are saved in metadata.segmentWinRules and applied when the player has matching analyticsTagSlugs on the games user.',
            'Aviator does not use this playerWinChance model.'
        ],
        editPerGameAlert:
            'These fields apply only to this game (“{gameCode}”). Saving updates that game’s document in MongoDB collection webGameConfigs and does not change other mini games.',
        cardWinChanceChip: 'Win chance {value}',
        cardEngagementOnChip: 'Loss-streak boost ON',
        cardEngagementOffChip: 'Loss-streak boost OFF',
        cardOpeningConfiguredChip: 'Welcome window set',
        cardOpeningDefaultChip: 'Welcome window off',
        sectionTitle: 'Win settings by user tag',
        sectionIntro:
            'Choose a user tag from Analytics, then set how generous the server should be for players with that tag and for how many sessions (rounds).',
        twoLayersIntro:
            'Players who match a configured tag use that row. Everyone else uses the default normal win chance below.',
        storageNote:
            'Save writes metadata.segmentWinRules and playerWinChance for this game in MongoDB webGameConfigs via PUT /api/dashboard/games/web-games/{gameCode}/config.',
        defaultsNote:
            'Add one row per tag. Each row needs a tag, a generosity target, and a session (round) cap.',
        aviatorWarning:
            'Aviator (aviator-simplified) does not use the shared playerWinChance / welcomeWindow / loss-streak model. Values below may be saved in metadata but do not drive Aviator round economics.',
        metadataMergeNote:
            'The fields above are merged into metadata on save. Use Advanced JSON only for game-specific keys not covered by the form.',
        layerOpeningTitle: 'Welcome generosity',
        layerOpeningIntro:
            'While the welcome window is active, the server favors the player until the favorable-win target or the round cap is reached. After that, normal win chance applies.',
        layerOpeningProgressNote:
            'Per-user welcome progress is stored in MongoDB collection webGameUserEngagementState, keyed by user and gameCode.',
        layerV1Title: 'Help after losses',
        layerV1Intro:
            'After enough consecutive losses in the same session, the server can add a small boost on top of the normal win chance, within the configured min/max bounds.',
        playerWinChanceLabel: 'Normal win chance (playerWinChance)',
        playerWinChanceHelper:
            'Target rate from 0 to 1 (default 0.45). Baseline “player favorable outcome” probability for layer 2; realized win rate still depends on the game and bet type.',
        engagementSwitchLabel: 'Enable help after losses (engagementTuningEnabled)',
        engagementSwitchCaption:
            'When on, consecutive losses in the session gradually raise effective win chance up to max boost, within min/max. When off, only the base player win chance applies (still clamped).',
        engagementMaxBoostLabel: 'Engagement max boost (engagementMaxBoost)',
        engagementMaxBoostHelper:
            'Upper limit (0–1, default 0.03) on extra probability added on top of the base chance during a loss streak.',
        engagementLossStreakLabel: 'Engagement loss streak step (engagementLossStreakStep)',
        engagementLossStreakHelper:
            'Integer ≥ 1 (default 3). Minimum consecutive losses before any boost applies.',
        engagementMinLabel: 'Engagement min player win chance (engagementMinPlayerWinChance)',
        engagementMinHelper:
            'Floor (0–1, default 0.15 in this editor). Clamps baseline and boosted effective win chance.',
        engagementMaxLabel: 'Engagement max player win chance (engagementMaxPlayerWinChance)',
        engagementMaxHelper:
            'Ceiling (0–1, default 0.60 in this editor). Clamps baseline and boosted effective win chance.',
        welcomeTitle: 'Welcome window (metadata.welcomeWindow)',
        welcomeIntro:
            'Set how many favorable wins to target and how many rounds are allowed while the welcome generosity window is active. Use 0 on either field to turn the window off.',
        welcomeFavorableWinsLabel: 'Welcome favorable wins target',
        welcomeMaxRoundsLabel: 'Welcome max rounds',
        welcomeHelper:
            'Both values must be integers ≥ 1 for an active window, or 0 to disable.',
        segmentRulesTitle: 'Generosity by user tag',
        segmentRulesIntro:
            'Each row applies to players who have the selected Analytics user tag. Set how many favorable wins to aim for and how many rounds (sessions) the generous window may last.',
        segmentRuleTagNamesLabel: 'User tag',
        segmentRuleTagNamesHelper:
            'Pick one tag from Analytics → User tags.',
        segmentRuleWelcomeFavorableWinsLabel: 'Generosity (favorable wins target)',
        segmentRuleWelcomeMaxRoundsLabel: 'Sessions (max generous rounds)',
        defaultWinChanceTitle: 'Default for everyone else',
        defaultWinChanceIntro:
            'Normal win chance when the player has no matching tag rule.',
        advancedMetadataTitle: 'Advanced JSON metadata',
        userTagsNote:
            'Tags are defined in Analytics. Games-server matches segmentWinRules when analyticsTagSlugs on the games user includes the tag slug.'
    },
    ro: {
        catalogEngagementHint:
            'Setările de câștig / engagement se fac per mini-joc: Games → Web Mini Games → Edit pe card. Nu există control global; fiecare joc are propria metadata în MongoDB.',
        catalogEngagementBullets: [
            'Alegi un user tag din Analytics, apoi setezi cât de generos e serverul pentru acel segment și pentru câte sesiuni (runde).',
            'Restul jucătorilor folosesc șansa normală implicită a jocului.',
            'Regulile se salvează în metadata.segmentWinRules și se aplică când analyticsTagSlugs pe userul din games conține tag-ul.',
            'Aviator nu folosește modelul playerWinChance.'
        ],
        editPerGameAlert:
            'Câmpurile se aplică doar acestui joc („{gameCode}”). Salvarea actualizează documentul din colecția MongoDB webGameConfigs și nu modifică celelalte mini-jocuri.',
        cardWinChanceChip: 'Țintă câștig {value}',
        cardEngagementOnChip: 'Boost pierderi ACTIV',
        cardEngagementOffChip: 'Boost pierderi OPRIT',
        cardOpeningConfiguredChip: 'Fereastră bun venit setată',
        cardOpeningDefaultChip: 'Fereastră bun venit oprită',
        sectionTitle: 'Câștig pe user tag',
        sectionIntro:
            'Alegi un user tag din Analytics, apoi setezi cât de generos e serverul pentru jucătorii cu acel tag și pentru câte sesiuni (runde).',
        twoLayersIntro:
            'Jucătorii care se potrivesc cu un rând configurat folosesc acea regulă. Restul folosesc șansa normală implicită de mai jos.',
        storageNote:
            'La Save se scriu metadata.segmentWinRules și playerWinChance pentru acest joc în MongoDB webGameConfigs, prin PUT /api/dashboard/games/web-games/{gameCode}/config.',
        defaultsNote:
            'Adaugă câte un rând per tag. Fiecare rând are nevoie de tag, țintă de generozitate și plafon de sesiuni (runde).',
        aviatorWarning:
            'Aviator (aviator-simplified) nu folosește modelul partajat playerWinChance / welcomeWindow / loss-streak. Valorile de mai jos pot fi salvate în metadata, dar nu guvernează economia rundei Aviator.',
        metadataMergeNote:
            'Câmpurile de mai sus sunt îmbinate în metadata la salvare. Folosește JSON avansat doar pentru chei specifice jocului care nu sunt în formular.',
        layerOpeningTitle: 'Generozitate la start',
        layerOpeningIntro:
            'Cât timp fereastra de bun venit e activă, serverul favorizează jucătorul până la ținta de câștiguri favorabile sau la plafonul de runde. Apoi intră șansa normală de câștig.',
        layerOpeningProgressNote:
            'Progresul per utilizator pentru fereastra de bun venit este în MongoDB, colecția webGameUserEngagementState, cheie logică utilizator + gameCode.',
        layerV1Title: 'Ajutor după pierderi',
        layerV1Intro:
            'După suficiente pierderi la rând în aceeași sesiune, serverul poate adăuga un boost mic peste șansa normală de câștig, în limitele min/max configurate.',
        playerWinChanceLabel: 'Șansă normală de câștig (playerWinChance)',
        playerWinChanceHelper:
            'Țintă între 0 și 1 (implicit 0,45). Probabilitate de bază pentru stratul 2; rata reală depinde totuși de joc și de tipul pariului.',
        engagementSwitchLabel: 'Activează ajutor după pierderi (engagementTuningEnabled)',
        engagementSwitchCaption:
            'Dacă e pornit, pierderile consecutive din sesiune cresc treptat șansa efectivă până la boost maxim, în limitele min/max. Dacă e oprit, rămâne doar playerWinChance de bază (tot plafonat).',
        engagementMaxBoostLabel: 'Boost maxim engagement (engagementMaxBoost)',
        engagementMaxBoostHelper:
            'Limita superioară (0–1, implicit 0,03) a probabilității suplimentare peste valoarea de bază într-o serie de pierderi.',
        engagementLossStreakLabel: 'Pas serie pierderi (engagementLossStreakStep)',
        engagementLossStreakHelper:
            'Număr întreg ≥ 1 (implicit 3). Pierderi consecutive minime înainte ca boost-ul să se aplice.',
        engagementMinLabel: 'Plafon minim (engagementMinPlayerWinChance)',
        engagementMinHelper:
            'Plafon jos (0–1, implicit 0,15 în acest editor). Limitează atât baza cât și șansa efectivă după boost.',
        engagementMaxLabel: 'Plafon maxim (engagementMaxPlayerWinChance)',
        engagementMaxHelper:
            'Plafon sus (0–1, implicit 0,60 în acest editor). Limitează atât baza cât și șansa efectivă după boost.',
        welcomeTitle: 'Fereastră bun venit (metadata.welcomeWindow)',
        welcomeIntro:
            'Setează câte câștiguri favorabile țintești și câte runde sunt permise cât timp fereastra de generozitate la start e activă. Folosește 0 la oricare câmp pentru a o dezactiva.',
        welcomeFavorableWinsLabel: 'Țintă câștiguri favorabile la bun venit',
        welcomeMaxRoundsLabel: 'Max runde la bun venit',
        welcomeHelper:
            'Ambele valori trebuie să fie întregi ≥ 1 pentru o fereastră activă, sau 0 pentru dezactivare.',
        segmentRulesTitle: 'Generozitate pe user tag',
        segmentRulesIntro:
            'Fiecare rând se aplică jucătorilor care au user tag-ul ales din Analytics. Setezi câte câștiguri favorabile țintești și câte runde (sesiuni) poate dura fereastra generoasă.',
        segmentRuleTagNamesLabel: 'User tag',
        segmentRuleTagNamesHelper:
            'Alege un singur tag din Analytics → User tags.',
        segmentRuleWelcomeFavorableWinsLabel: 'Generozitate (țintă câștiguri favorabile)',
        segmentRuleWelcomeMaxRoundsLabel: 'Sesiuni (max runde generoase)',
        defaultWinChanceTitle: 'Implicit pentru restul jucătorilor',
        defaultWinChanceIntro:
            'Șansa normală de câștig când jucătorul nu are un tag configurat mai sus.',
        advancedMetadataTitle: 'Metadata JSON avansată',
        userTagsNote:
            'Tag-urile sunt definite în Analytics. Games-server potrivește segmentWinRules când analyticsTagSlugs pe userul din games include slug-ul tag-ului.'
    }
};

const WEB_GAMES_SOUND_UI: Record<WebGamesUiLang, {
    sectionTitle: string;
    sectionIntro: string;
    storageNote: string;
    backgroundMusicTitle: string;
    backgroundMusicIntro: string;
    backgroundMusicPathLabel: string;
    backgroundMusicPathHelper: string;
    backgroundMusicPathPlaceholder: string;
    backgroundMusicNoneOption: string;
    backgroundMusicVolumeLabel: string;
    backgroundMusicVolumeHelper: string;
    backgroundMusicPlaybackRateLabel: string;
    backgroundMusicPlaybackRateHelper: string;
    sfxTitle: string;
    sfxIntro: string;
    sfxRollLabel: string;
    sfxWinLabel: string;
    sfxLossLabel: string;
    sfxMomentHelper: string;
    sfxPathPlaceholder: string;
    sfxNoneOption: string;
    soundAssetsLoading: string;
    soundAssetsEmpty: string;
    uploadButton: string;
    uploadHelper: string;
    uploadFailed: string;
    uploadSuccess: string;
    refreshCatalog: string;
    previewLabel: string;
    previewUnavailable: string;
    previewOpenLinkLabel: string;
    strapiFolderNote: string;
    momentsInventoryTitle: string;
    momentsInventoryIntro: string;
    momentDurationColumn: string;
    momentDefaultPathColumn: string;
    momentConfigurableColumn: string;
    momentConfigurableYes: string;
    momentConfigurableNo: string;
    momentPickerDurationPrefix: string;
}> = {
    en: {
        sectionTitle: 'Soundtrack / SFX (per game)',
        sectionIntro:
            'Background music and moment SFX are stored in metadata.backgroundMusic and metadata.sfxCatalog. Templates read them from GET /config when implemented; other games keep built-in paths until wired.',
        storageNote:
            'Upload new audio via the shared uploads API (uploads_api/webgames), or pick built-in files from the games-server catalog. Save stores full URLs or /webgames paths in metadata. Dice reads them from GET /config; other templates keep built-in paths until wired.',
        backgroundMusicTitle: 'Background music (metadata.backgroundMusic)',
        backgroundMusicIntro: 'Optional looped track. Leave track unset to disable background music for this game.',
        backgroundMusicPathLabel: 'Background track',
        backgroundMusicPathHelper: 'Pick from the catalog, or paste uploads_api/webgames paths, /webgames paths, full URLs, or Strapi public download links (/api/public/dl/...). Preview resolves /webgames against the game URL origin (or REACT_APP_GAMES_SERVER_URL).',
        backgroundMusicPathPlaceholder: 'Pick or paste a sound path or URL',
        backgroundMusicNoneOption: 'None',
        backgroundMusicVolumeLabel: 'Background volume (0–1)',
        backgroundMusicVolumeHelper: 'Default 0.4 when omitted.',
        backgroundMusicPlaybackRateLabel: 'Background playback rate',
        backgroundMusicPlaybackRateHelper: 'Optional speed multiplier (0.5–2). Default 1.',
        sfxTitle: 'Moment SFX (metadata.sfxCatalog)',
        sfxIntro:
            'Map common moment keys to catalog files. Dice uses roll / win / loss; other templates can reuse the same keys or read custom entries from metadata JSON.',
        sfxRollLabel: 'SFX roll',
        sfxWinLabel: 'SFX win',
        sfxLossLabel: 'SFX loss',
        sfxMomentHelper: 'Empty keeps the template default path for that moment.',
        sfxPathPlaceholder: 'Pick or paste a sound path or URL',
        sfxNoneOption: 'Template default',
        soundAssetsLoading: 'Loading sound catalog…',
        soundAssetsEmpty: 'No built-in sound files from the games server yet. Upload audio to add options.',
        uploadButton: 'Upload audio file',
        uploadHelper: 'Uploads use the same dashboard file API as sport images (uploads_api/webgames). Accepted: MP3, OGG, WAV, M4A.',
        uploadFailed: 'Failed to upload the sound file.',
        uploadSuccess: 'Sound uploaded. Select it from the dropdown and save the game config.',
        refreshCatalog: 'Refresh built-in catalog',
        previewLabel: 'Preview',
        previewUnavailable: 'Preview is unavailable for this path. Check that the games server origin is reachable and uploads_api files are served from the API host.',
        previewOpenLinkLabel: 'Open audio URL in a new tab',
        strapiFolderNote: 'Optional Strapi folder listing uses REACT_APP_STRAPI_WEBGAMES_FOLDER when set.',
        momentsInventoryTitle: 'Sound moments for this game',
        momentsInventoryIntro: 'Typical clip length helps you pick a matching asset. Only moments marked configurable are written from the fields below; others stay on template defaults until wired.',
        momentDurationColumn: 'Typical length',
        momentDefaultPathColumn: 'Default path',
        momentConfigurableColumn: 'Dashboard',
        momentConfigurableYes: 'Configurable',
        momentConfigurableNo: 'Template only',
        momentPickerDurationPrefix: 'Typical length'
    },
    ro: {
        sectionTitle: 'Coloană sonoră / SFX (per joc)',
        sectionIntro:
            'Muzica de fundal și SFX-urile pe moment sunt în metadata.backgroundMusic și metadata.sfxCatalog. Template-urile le citesc din GET /config unde e implementat; restul jocurilor păstrează căile implicite până sunt legate.',
        storageNote:
            'Încarcă audio prin API-ul partajat uploads_api (webgames) sau alege fișiere built-in din catalogul games-server. La Save se salvează URL-uri complete sau căi /webgames în metadata. Dice le citește din GET /config; restul template-urilor păstrează căile implicite până sunt legate.',
        backgroundMusicTitle: 'Muzică de fundal (metadata.backgroundMusic)',
        backgroundMusicIntro: 'Pistă opțională în buclă. Lasă neales pentru a dezactiva muzica de fundal la acest joc.',
        backgroundMusicPathLabel: 'Pistă fundal',
        backgroundMusicPathHelper: 'Alege din catalog sau lipește căi uploads_api/webgames, căi /webgames, URL-uri complete sau linkuri publice Strapi (/api/public/dl/...). Preview rezolvă /webgames față de originea gameUrl (sau REACT_APP_GAMES_SERVER_URL).',
        backgroundMusicPathPlaceholder: 'Alege sau lipește o cale sau URL audio',
        backgroundMusicNoneOption: 'Niciuna',
        backgroundMusicVolumeLabel: 'Volum fundal (0–1)',
        backgroundMusicVolumeHelper: 'Implicit 0,4 dacă lipsește.',
        backgroundMusicPlaybackRateLabel: 'Viteză redare fundal',
        backgroundMusicPlaybackRateHelper: 'Multiplicator opțional (0,5–2). Implicit 1.',
        sfxTitle: 'SFX pe moment (metadata.sfxCatalog)',
        sfxIntro:
            'Mapează chei de moment la fișiere din catalog. Dice folosește roll / win / loss; alte template-uri pot refolosi cheile sau citi intrări custom din JSON metadata.',
        sfxRollLabel: 'SFX aruncare',
        sfxWinLabel: 'SFX câștig',
        sfxLossLabel: 'SFX pierdere',
        sfxMomentHelper: 'Gol păstrează calea implicită din template pentru acel moment.',
        sfxPathPlaceholder: 'Alege sau lipește o cale sau URL audio',
        sfxNoneOption: 'Implicit template',
        soundAssetsLoading: 'Se încarcă catalogul de sunete…',
        soundAssetsEmpty: 'Nu există încă fișiere built-in de la games-server. Încarcă audio pentru a adăuga opțiuni.',
        uploadButton: 'Încarcă fișier audio',
        uploadHelper: 'Încărcarea folosește același API de fișiere ca imaginile sport (uploads_api/webgames). Formate: MP3, OGG, WAV, M4A.',
        uploadFailed: 'Încărcarea fișierului audio a eșuat.',
        uploadSuccess: 'Sunet încărcat. Alege-l din listă și salvează config-ul jocului.',
        refreshCatalog: 'Reîmprospătează catalogul built-in',
        previewLabel: 'Previzualizare',
        previewUnavailable: 'Previzualizarea nu este disponibilă pentru această cale. Verifică originea games-server și că fișierele uploads_api sunt servite de hostul API.',
        previewOpenLinkLabel: 'Deschide URL-ul audio într-un tab nou',
        strapiFolderNote: 'Listarea opțională din folderul Strapi folosește REACT_APP_STRAPI_WEBGAMES_FOLDER când este setat.',
        momentsInventoryTitle: 'Momente sonore pentru acest joc',
        momentsInventoryIntro: 'Durata tipică te ajută să alegi un asset potrivit. Doar momentele marcate configurabile sunt salvate din câmpurile de mai jos; restul rămân pe valorile implicite din template până sunt legate.',
        momentDurationColumn: 'Durată tipică',
        momentDefaultPathColumn: 'Cale implicită',
        momentConfigurableColumn: 'Dashboard',
        momentConfigurableYes: 'Configurabil',
        momentConfigurableNo: 'Doar template',
        momentPickerDurationPrefix: 'Durată tipică'
    }
};

const WEB_GAMES_COVER_IMAGE_UI: Record<WebGamesUiLang, {
    label: string;
    helper: string;
    pickerHelper: string;
    collectionTooltip: string;
    collectionTitle: string;
    collectionManage: string;
    collectionDone: string;
    collectionEmpty: string;
    collectionProjectSection: string;
    collectionUploadFolderSection: string;
    collectionStrapiSection: string;
    collectionDeleteAria: string;
    collectionOpenAria: string;
    addCurrentToCollection: string;
    addCurrentSuccess: string;
    addCurrentFailed: string;
    deleteFailed: string;
    previewLabel: string;
    previewUnavailable: string;
    strapiFolderNote: string;
    refreshCatalog: string;
}> = {
    en: {
        label: 'Cover image URL',
        helper: 'Stored as coverImageUrl on the game config. Pick from the image library, paste an uploads_api/webgames path, a full URL, or a Strapi media path.',
        pickerHelper: 'Upload new cover files outside the dashboard into uploads_api/webgames, then refresh the image library and pick from the collection.',
        collectionTooltip: 'Pick cover from collection',
        collectionTitle: 'Cover image collection',
        collectionManage: 'Manage',
        collectionDone: 'Done',
        collectionEmpty: 'No cover images in the collection yet.',
        collectionProjectSection: 'In use in project',
        collectionUploadFolderSection: 'Uploads folder',
        collectionStrapiSection: 'Strapi media',
        collectionDeleteAria: 'Delete cover image preset',
        collectionOpenAria: 'Open cover image collection',
        addCurrentToCollection: 'Add current URL to collection',
        addCurrentSuccess: 'Cover image added to the collection.',
        addCurrentFailed: 'Cover image URL was not added to the collection.',
        deleteFailed: 'Cover image was not removed from the collection.',
        previewLabel: 'Preview',
        previewUnavailable: 'Preview is unavailable for this path.',
        strapiFolderNote: 'Optional Strapi folder listing uses REACT_APP_STRAPI_WEBGAMES_FOLDER when set.',
        refreshCatalog: 'Refresh image library',
    },
    ro: {
        label: 'URL imagine copertă',
        helper: 'Salvat ca coverImageUrl în config-ul jocului. Alege din biblioteca de imagini, lipește o cale uploads_api/webgames, un URL complet sau o cale media Strapi.',
        pickerHelper: 'Încarcă fișiere noi de copertă în afara dashboard-ului în uploads_api/webgames, apoi reîmprospătează biblioteca de imagini și alege din colecție.',
        collectionTooltip: 'Alege coperta din colecție',
        collectionTitle: 'Colecție imagini copertă',
        collectionManage: 'Administrează',
        collectionDone: 'Gata',
        collectionEmpty: 'Nu există încă imagini de copertă în colecție.',
        collectionProjectSection: 'Folosite în proiect',
        collectionUploadFolderSection: 'Folder uploads',
        collectionStrapiSection: 'Media Strapi',
        collectionDeleteAria: 'Șterge preset imagine copertă',
        collectionOpenAria: 'Deschide colecția de imagini copertă',
        addCurrentToCollection: 'Adaugă URL-ul curent în colecție',
        addCurrentSuccess: 'Imaginea de copertă a fost adăugată în colecție.',
        addCurrentFailed: 'URL-ul imaginii de copertă nu a fost adăugat în colecție.',
        deleteFailed: 'Imaginea de copertă nu a fost eliminată din colecție.',
        previewLabel: 'Previzualizare',
        previewUnavailable: 'Previzualizarea nu este disponibilă pentru această cale.',
        strapiFolderNote: 'Listarea opțională din folderul Strapi folosește REACT_APP_STRAPI_WEBGAMES_FOLDER când este setat.',
        refreshCatalog: 'Reîmprospătează biblioteca de imagini',
    }
};

const WEB_GAMES_SLOT_SYMBOL_UI: Record<WebGamesUiLang, {
    sectionTitle: string;
    sectionIntro: string;
    imageUrlLabel: string;
    imageUrlHelper: string;
    uploadsFolderHelper: string;
    previewLabel: string;
    previewUnavailable: string;
    defaultPathLabel: string;
    uploadsPathLabel: string;
}> = {
    en: {
        sectionTitle: 'Slot reel symbols (metadata.symbols)',
        sectionIntro:
            'Each row maps a stable symbol id to an image URL served by the games host. Empty fields fall back to bundled defaults in slot-machine.html.',
        imageUrlLabel: 'Image URL',
        imageUrlHelper: 'Pick from the image library or set uploads_api/webgames paths, /webgames/assets paths, full URLs, or Strapi media paths.',
        uploadsFolderHelper: `Place symbol files under ${SLOT_MACHINE_SYMBOL_UPLOADS_FOLDER}/ (for example ${slotMachineSymbolUploadStoredPath('soccer')}), refresh the image library, then pick them here. The dashboard does not upload symbol images.`,
        previewLabel: 'Preview',
        previewUnavailable: 'Preview is unavailable for this path.',
        defaultPathLabel: 'Bundled default',
        uploadsPathLabel: 'Suggested uploads path',
    },
    ro: {
        sectionTitle: 'Simboluri role (metadata.symbols)',
        sectionIntro:
            'Fiecare rând leagă un id stabil de simbol de un URL de imagine servit de games-server. Câmpurile goale folosesc valorile implicite din slot-machine.html.',
        imageUrlLabel: 'URL imagine',
        imageUrlHelper: 'Alege din biblioteca de imagini sau setează căi uploads_api/webgames, /webgames/assets, URL-uri complete sau căi media Strapi.',
        uploadsFolderHelper: `Plasează fișierele de simbol în ${SLOT_MACHINE_SYMBOL_UPLOADS_FOLDER}/ (de exemplu ${slotMachineSymbolUploadStoredPath('soccer')}), reîmprospătează biblioteca de imagini, apoi alege-le aici. Dashboard-ul nu încarcă imagini de simbol.`,
        previewLabel: 'Previzualizare',
        previewUnavailable: 'Previzualizarea nu este disponibilă pentru această cale.',
        defaultPathLabel: 'Implicit în pachet',
        uploadsPathLabel: 'Cale uploads sugerată',
    }
};

const gamesServerOriginFromEnv = (): string => {
    const raw = process.env.REACT_APP_GAMES_SERVER_URL?.trim();
    if (!raw) return '';
    try {
        return new URL(raw).origin;
    } catch {
        return '';
    }
};

const resolveWebGameCoverImageUrl = (
    pathOrUrl: string | null | undefined,
    options?: { gamesServerOrigin?: string }
): string => {
    if (pathOrUrl == null) return '';
    const normalized = pathOrUrl.trim();
    if (!normalized) return '';

    const gamesOrigin = (options?.gamesServerOrigin || gamesServerOriginFromEnv()).replace(/\/$/, '');

    if (isWebGameUploadsApiPath(normalized)) {
        const uploadsUrl = resolveWebGameUploadsPublicUrl(normalized);
        if (uploadsUrl) {
            return uploadsUrl;
        }
        if (gamesOrigin) {
            const rel = normalizeWebGameUploadStoredPath(normalized).replace(/^\/*/, '');
            return `${gamesOrigin}/${rel}`;
        }
    }

    if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        return normalized;
    }

    const resolved = resolveStrapiMediaUrlForDisplay(normalized);
    if (resolved && (resolved.startsWith('http://') || resolved.startsWith('https://'))) {
        return resolved;
    }
    if (gamesOrigin && normalized.startsWith('/')) {
        return `${gamesOrigin}${normalized}`;
    }
    return resolved ?? normalized;
};

const normalizeCoverImageCollectionUrl = normalizeWebGameUploadCollectionStoredPath;
const normalizeCoverImageStoredUrl = normalizeWebGameCoverImageStoredPath;
const normalizeSoundAssetStoredPath = (pathOrUrl: string | null | undefined): string => {
    if (pathOrUrl == null) return '';
    const trimmed = pathOrUrl.trim();
    if (!trimmed) return '';
    if (isWebGameUploadsApiPath(trimmed)) {
        return normalizeWebGameUploadStoredPath(trimmed);
    }
    return normalizeCompetitionBackgroundUrl(trimmed) ?? '';
};

const numberFromMetadata = (metadata: Record<string, unknown>, key: string): string => {
    const value = metadata?.[key];
    return typeof value === 'number' || typeof value === 'string' ? String(value) : '-';
};

const parseJsonObject = (value: string, fieldName: string): Record<string, unknown> => {
    const parsed = JSON.parse(value || '{}');
    if (parsed == null || Array.isArray(parsed) || typeof parsed !== 'object') {
        throw new Error(`${fieldName} must be a JSON object.`);
    }
    return parsed as Record<string, unknown>;
};

const numberFromUnknown = (value: unknown, fallback: number): number => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
};

const booleanFromUnknown = (value: unknown, fallback: boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const lower = value.trim().toLowerCase();
        if (lower === 'true') return true;
        if (lower === 'false') return false;
    }
    return fallback;
};

const formatCardWinChance = (game: WebGameAdminConfigDto): string => {
    const v = numberFromUnknown(game.metadata?.playerWinChance, DEFAULT_PLAYER_WIN_CHANCE);
    return Number.isFinite(v) ? v.toFixed(2) : String(DEFAULT_PLAYER_WIN_CHANCE);
};

const isOpeningRuleActive = (rule: unknown): boolean => {
    if (rule == null || typeof rule !== 'object' || Array.isArray(rule)) return false;
    const map = rule as Record<string, unknown>;
    const favorableWinsTarget = numberFromUnknown(map.favorableWinsTarget, 0);
    const maxRoundsInWindow = numberFromUnknown(map.maxRoundsInWindow, 0);
    return favorableWinsTarget >= 1 && maxRoundsInWindow >= 1;
};

const hasConfiguredWelcomeWindow = (metadata: Record<string, unknown> | undefined): boolean => {
    const welcomeWindow = metadata?.welcomeWindow;
    if (welcomeWindow != null && typeof welcomeWindow === 'object' && !Array.isArray(welcomeWindow)) {
        const map = welcomeWindow as Record<string, unknown>;
        const favorableWinsTarget = numberFromUnknown(map.favorableWinsTarget, 0);
        const maxRoundsInWindow = numberFromUnknown(map.maxRoundsInWindow, 0);
        if (favorableWinsTarget >= 1 && maxRoundsInWindow >= 1) {
            return true;
        }
    }
    return hasConfiguredOpeningRules(metadata);
};

const hasConfiguredOpeningRules = (metadata: Record<string, unknown> | undefined): boolean => {
    const rawRules = metadata?.openingRules;
    if (!Array.isArray(rawRules)) return false;
    return rawRules.some(isOpeningRuleActive);
};

const parseNumberInput = (value: string, fieldName: string): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        throw new Error(`${fieldName} must be a valid number.`);
    }
    return parsed;
};

type OpeningRuleEditor = {
    favorableWinsTarget: number;
    maxRoundsInWindow: number;
};

const welcomeWindowFromMetadata = (metadata: Record<string, unknown>, fallback: OpeningRuleEditor): OpeningRuleEditor => {
    const welcomeWindow = metadata?.welcomeWindow;
    if (welcomeWindow != null && typeof welcomeWindow === 'object' && !Array.isArray(welcomeWindow)) {
        const map = welcomeWindow as Record<string, unknown>;
        return {
            favorableWinsTarget: numberFromUnknown(map.favorableWinsTarget, fallback.favorableWinsTarget),
            maxRoundsInWindow: numberFromUnknown(map.maxRoundsInWindow, fallback.maxRoundsInWindow)
        };
    }
    return openingRuleFromMetadata(metadata, 1, fallback);
};

const emptySegmentWinRule = (): SegmentWinRuleEditor => ({
    tagNames: [],
    welcomeFavorableWinsTarget: '',
    welcomeMaxRoundsInWindow: ''
});

const segmentRulesFromMetadata = (metadata: Record<string, unknown>): SegmentWinRuleEditor[] => {
    const rawRules = metadata?.segmentWinRules;
    if (!Array.isArray(rawRules)) {
        return [];
    }
    return rawRules.map(rawRule => {
        if (rawRule == null || typeof rawRule !== 'object' || Array.isArray(rawRule)) {
            return emptySegmentWinRule();
        }
        const map = rawRule as Record<string, unknown>;
        const tagNames = Array.isArray(map.tagNames)
            ? map.tagNames.map(tag => String(tag).trim().toLowerCase()).filter(tag => tag.length > 0).slice(0, 1)
            : typeof map.tagNames === 'string'
                ? map.tagNames.split(',').map(tag => tag.trim().toLowerCase()).filter(tag => tag.length > 0).slice(0, 1)
                : [];
        const welcomeWindow = map.welcomeWindow;
        const welcomeMap = welcomeWindow != null && typeof welcomeWindow === 'object' && !Array.isArray(welcomeWindow)
            ? welcomeWindow as Record<string, unknown>
            : {};
        return {
            tagNames,
            welcomeFavorableWinsTarget: welcomeMap.favorableWinsTarget == null ? '' : String(numberFromUnknown(welcomeMap.favorableWinsTarget, 0)),
            welcomeMaxRoundsInWindow: welcomeMap.maxRoundsInWindow == null ? '' : String(numberFromUnknown(welcomeMap.maxRoundsInWindow, 0))
        };
    });
};

const sfxPathsFromMetadata = (game: WebGameAdminConfigDto): Record<string, string> => {
    const profile = webGameSoundProfileForGame(game.code, game.htmlTemplateName);
    const metadata = game.metadata ?? {};
    const paths: Record<string, string> = {};
    profile?.moments
        .filter(moment => moment.configurableInDashboard && moment.binding === 'sfxCatalog')
        .forEach(moment => {
            paths[moment.key] = sfxPathFromMetadata(metadata, moment.key);
        });
    return paths;
};

const openingRuleFromMetadata = (metadata: Record<string, unknown>, entry: number, fallback: OpeningRuleEditor): OpeningRuleEditor => {
    const rawRules = metadata?.openingRules;
    if (!Array.isArray(rawRules)) {
        return fallback;
    }
    const rawRule = rawRules.find(rule => {
        if (rule == null || typeof rule !== 'object' || Array.isArray(rule)) return false;
        const value = numberFromUnknown((rule as Record<string, unknown>).entry, -1);
        return value === entry;
    });
    if (rawRule == null || typeof rawRule !== 'object' || Array.isArray(rawRule)) {
        return fallback;
    }
    const map = rawRule as Record<string, unknown>;
    return {
        favorableWinsTarget: numberFromUnknown(map.favorableWinsTarget, fallback.favorableWinsTarget),
        maxRoundsInWindow: numberFromUnknown(map.maxRoundsInWindow, fallback.maxRoundsInWindow)
    };
};

const stringFromMetadataObject = (metadata: Record<string, unknown>, key: string, nestedKey: string): string => {
    const nested = metadata?.[key];
    if (nested == null || typeof nested !== 'object' || Array.isArray(nested)) return '';
    const value = (nested as Record<string, unknown>)[nestedKey];
    return typeof value === 'string' ? value : '';
};

const sfxPathFromMetadata = (metadata: Record<string, unknown>, momentKey: string): string => {
    const rawCatalog = metadata?.sfxCatalog;
    if (rawCatalog == null || typeof rawCatalog !== 'object' || Array.isArray(rawCatalog)) return '';
    const value = (rawCatalog as Record<string, unknown>)[momentKey];
    return typeof value === 'string' ? value : '';
};

const WebGameCoverImagePresetPicker: React.FC<{
    value: string;
    presets: WebGameImagePresetDto[];
    projectImageUrls: string[];
    uploadedFolderImageUrls: string[];
    strapiImages: StrapiFolderImage[];
    gamesServerOrigin?: string;
    copy: typeof WEB_GAMES_COVER_IMAGE_UI.en;
    onSelect: (url: string) => void;
    onDelete: (preset: WebGameImagePresetDto) => Promise<void>;
}> = ({ value, presets, projectImageUrls, uploadedFolderImageUrls, strapiImages, gamesServerOrigin, copy, onSelect, onDelete }) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [manage, setManage] = React.useState(false);
    const open = Boolean(anchorEl);

    const close = () => {
        setAnchorEl(null);
        setManage(false);
    };

    const presetUrls = useMemo(() => new Set(presets.map(preset => preset.url.trim()).filter(Boolean)), [presets]);
    const projectOnly = useMemo(
        () => projectImageUrls.filter(url => !presetUrls.has(url)),
        [projectImageUrls, presetUrls]
    );
    const uploadFolderOnly = useMemo(
        () => uploadedFolderImageUrls.filter(url => !presetUrls.has(url) && !projectOnly.includes(url)),
        [uploadedFolderImageUrls, presetUrls, projectOnly]
    );
    const strapiOnly = useMemo(
        () => strapiImages
            .map(image => image.url.trim())
            .filter(url => url && !presetUrls.has(url) && !projectOnly.includes(url) && !uploadFolderOnly.includes(url)),
        [strapiImages, presetUrls, projectOnly, uploadFolderOnly]
    );

    const renderImageTile = (url: string, key: string, deletablePreset?: WebGameImagePresetDto) => {
        const selected = url === value;
        return (
            <Box
                key={key}
                onClick={() => {
                    if (manage) return;
                    onSelect(url);
                    close();
                }}
                sx={{
                    position: 'relative',
                    height: 72,
                    borderRadius: 1,
                    border: '2px solid',
                    borderColor: selected ? 'primary.main' : 'divider',
                    overflow: 'hidden',
                    cursor: manage ? 'default' : 'pointer',
                    bgcolor: 'background.default',
                }}
            >
                <Box
                    component="img"
                    src={resolveWebGameCoverImageUrl(url, { gamesServerOrigin }) || url}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {manage && deletablePreset ? (
                    <IconButton
                        size="small"
                        color="error"
                        onClick={event => {
                            event.stopPropagation();
                            void onDelete(deletablePreset);
                        }}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'background.paper' },
                        }}
                        aria-label={copy.collectionDeleteAria}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                ) : null}
            </Box>
        );
    };

    const imageGridSx = {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 1,
        maxHeight: 320,
        overflowY: 'auto',
        pr: 0.5,
    } as const;

    return (
        <>
            <Tooltip title={copy.collectionTooltip}>
                <span>
                    <IconButton
                        color={value ? 'primary' : 'default'}
                        onClick={event => setAnchorEl(event.currentTarget)}
                        aria-label={copy.collectionOpenAria}
                    >
                        <CollectionsIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ width: 360, maxWidth: '90vw', p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">{copy.collectionTitle}</Typography>
                        <Button size="small" onClick={() => setManage(prev => !prev)}>
                            {manage ? copy.collectionDone : copy.collectionManage}
                        </Button>
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    {presets.length === 0 && projectOnly.length === 0 && uploadFolderOnly.length === 0 && strapiOnly.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                            {copy.collectionEmpty}
                        </Typography>
                    ) : (
                        <Stack spacing={1.5}>
                            {presets.length > 0 ? (
                                <Box sx={imageGridSx}>
                                    {presets.map(preset => renderImageTile(preset.url, preset.id, preset))}
                                </Box>
                            ) : null}
                            {projectOnly.length > 0 ? (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {copy.collectionProjectSection}
                                    </Typography>
                                    <Box sx={imageGridSx}>
                                        {projectOnly.map(url => renderImageTile(url, `project-${url}`))}
                                    </Box>
                                </Box>
                            ) : null}
                            {uploadFolderOnly.length > 0 ? (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {copy.collectionUploadFolderSection}
                                    </Typography>
                                    <Box sx={imageGridSx}>
                                        {uploadFolderOnly.map(url => renderImageTile(url, `uploads-${url}`))}
                                    </Box>
                                </Box>
                            ) : null}
                            {strapiOnly.length > 0 ? (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                        {copy.collectionStrapiSection}
                                    </Typography>
                                    <Box sx={imageGridSx}>
                                        {strapiOnly.map(url => renderImageTile(url, `strapi-${url}`))}
                                    </Box>
                                </Box>
                            ) : null}
                        </Stack>
                    )}
                </Box>
            </Popover>
        </>
    );
};

const CoverImagePreview: React.FC<{ label: string; source: string; unavailableText: string }> = ({ label, source, unavailableText }) => {
    if (!source) {
        return (
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {label}
                </Typography>
                <Typography variant="body2" color="text.secondary">{unavailableText}</Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Box
                component="img"
                src={source}
                alt=""
                sx={{ width: '100%', maxHeight: 180, objectFit: 'contain', borderRadius: 1, bgcolor: 'background.default' }}
            />
        </Box>
    );
};

const SoundPreview: React.FC<{ label: string; source: string; unavailableText: string; openLinkLabel: string }> = ({
    label,
    source,
    unavailableText,
    openLinkLabel,
}) => {
    if (!source) {
        return (
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {label}
                </Typography>
                <Typography variant="body2" color="text.secondary">{unavailableText}</Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Box component="audio" key={source} controls preload="metadata" src={source} sx={{ width: '100%' }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                <Link href={source} target="_blank" rel="noopener noreferrer">
                    {openLinkLabel}
                </Link>
            </Typography>
        </Box>
    );
};

const soundMomentPickerHelper = (
    moment: WebGameSoundMomentDefinition | undefined,
    fallbackHelper: string,
    lang: WebGameSoundUiLang,
    durationPrefix: string
): string => {
    if (!moment) return fallbackHelper;
    const duration = formatWebGameSoundMomentDuration(moment.typicalDurationSeconds, lang);
    return `${moment.description[lang]} ${durationPrefix}: ${duration}.`;
};

const WebGameSoundMomentsInventory: React.FC<{
    moments: WebGameSoundMomentDefinition[];
    lang: WebGameSoundUiLang;
    copy: typeof WEB_GAMES_SOUND_UI.en;
}> = ({ moments, lang, copy }) => (
    <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            {copy.momentsInventoryTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {copy.momentsInventoryIntro}
        </Typography>
        <Stack spacing={1}>
            {moments.map(moment => (
                <Box
                    key={moment.key}
                    sx={{
                        border: theme => `1px solid ${theme.palette.divider}`,
                        borderRadius: '6px',
                        p: 1,
                        bgcolor: theme => theme.palette.action.hover
                    }}
                >
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Typography variant="body2" fontWeight={700}>
                            {moment.labels[lang]} ({moment.key})
                        </Typography>
                        <Chip
                            size="small"
                            color={moment.configurableInDashboard ? 'success' : 'default'}
                            label={moment.configurableInDashboard ? copy.momentConfigurableYes : copy.momentConfigurableNo}
                        />
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {moment.description[lang]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {copy.momentDurationColumn}: {formatWebGameSoundMomentDuration(moment.typicalDurationSeconds, lang)}
                        {moment.defaultPath ? ` · ${copy.momentDefaultPathColumn}: ${moment.defaultPath}` : ''}
                    </Typography>
                </Box>
            ))}
        </Stack>
    </Box>
);

const toEditorState = (game: WebGameAdminConfigDto): EditorState => ({
    ...(() => {
        const welcome = welcomeWindowFromMetadata(game.metadata ?? {}, {
            favorableWinsTarget: DEFAULT_WELCOME_FAVORABLE_WINS_TARGET,
            maxRoundsInWindow: DEFAULT_WELCOME_MAX_ROUNDS_IN_WINDOW
        });
        return {
            welcomeFavorableWinsTarget: String(welcome.favorableWinsTarget),
            welcomeMaxRoundsInWindow: String(welcome.maxRoundsInWindow),
            segmentRules: segmentRulesFromMetadata(game.metadata ?? {}),
            soundUploadPrefix: game.code,
        };
    })(),
    name: game.name,
    description: game.description ?? '',
    enabled: game.enabled,
    order: game.order,
    coverImageUrl: normalizeCoverImageStoredUrl(game.coverImageUrl ?? ''),
    htmlTemplateName: game.htmlTemplateName,
    localizationsText: JSON.stringify(game.localizations ?? {}, null, 2),
    descriptionLocalizationsText: JSON.stringify(game.descriptionLocalizations ?? {}, null, 2),
    metadataText: JSON.stringify(game.metadata ?? {}, null, 2),
    playerWinChance: String(numberFromUnknown(game.metadata?.playerWinChance, DEFAULT_PLAYER_WIN_CHANCE)),
    engagementTuningEnabled: booleanFromUnknown(game.metadata?.engagementTuningEnabled, DEFAULT_ENGAGEMENT_TUNING_ENABLED),
    engagementMaxBoost: String(numberFromUnknown(game.metadata?.engagementMaxBoost, DEFAULT_ENGAGEMENT_MAX_BOOST)),
    engagementLossStreakStep: String(numberFromUnknown(game.metadata?.engagementLossStreakStep, DEFAULT_ENGAGEMENT_LOSS_STREAK_STEP)),
    engagementMinPlayerWinChance: String(numberFromUnknown(game.metadata?.engagementMinPlayerWinChance, DEFAULT_ENGAGEMENT_MIN_PLAYER_WIN_CHANCE)),
    engagementMaxPlayerWinChance: String(numberFromUnknown(game.metadata?.engagementMaxPlayerWinChance, DEFAULT_ENGAGEMENT_MAX_PLAYER_WIN_CHANCE)),
    backgroundMusicPath: normalizeSoundAssetStoredPath(
        stringFromMetadataObject(game.metadata ?? {}, 'backgroundMusic', 'path')
    ),
    backgroundMusicVolume: String(numberFromUnknown(
        (game.metadata?.backgroundMusic as Record<string, unknown> | undefined)?.volume,
        DEFAULT_BACKGROUND_MUSIC_VOLUME
    )),
    backgroundMusicPlaybackRate: String(numberFromUnknown(
        (game.metadata?.backgroundMusic as Record<string, unknown> | undefined)?.playbackRate,
        DEFAULT_BACKGROUND_MUSIC_PLAYBACK_RATE
    )),
    slotSymbolImages: game.code === SLOT_MACHINE_GAME_CODE
        ? slotSymbolImagesFromMetadata(game.metadata ?? {})
        : [],
    sfxPaths: Object.fromEntries(
        Object.entries(sfxPathsFromMetadata(game)).map(([momentKey, path]) => [
            momentKey,
            normalizeSoundAssetStoredPath(path)
        ])
    )
});

const WebGamesPage: React.FC = () => {
    const uiLang = useMemo(() => webGamesUiLangFromBrowser(), []);
    const egCopy = WEB_GAMES_ENGAGEMENT_UI[uiLang];
    const soundCopy = WEB_GAMES_SOUND_UI[uiLang];
    const coverCopy = WEB_GAMES_COVER_IMAGE_UI[uiLang];
    const slotSymbolCopy = WEB_GAMES_SLOT_SYMBOL_UI[uiLang];
    const [games, setGames] = useState<WebGameAdminConfigDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [selectedGame, setSelectedGame] = useState<WebGameAdminConfigDto | null>(null);
    const [editor, setEditor] = useState<EditorState>(emptyEditor);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [playGame, setPlayGame] = useState<WebGameAdminConfigDto | null>(null);
    const [playUserIdText, setPlayUserIdText] = useState<string>('');
    const [playUserInfo, setPlayUserInfo] = useState<AggregateUserInfoDto | null>(null);
    const [playUserLoading, setPlayUserLoading] = useState<boolean>(false);
    const [playLaunching, setPlayLaunching] = useState<boolean>(false);
    const [playError, setPlayError] = useState<string | null>(null);
    const [userSearchTerm, setUserSearchTerm] = useState<string>('');
    const [userOptions, setUserOptions] = useState<AccountMainInfoDto[]>([]);
    const [userOptionsLoading, setUserOptionsLoading] = useState<boolean>(false);
    const [soundAssets, setSoundAssets] = useState<string[]>([]);
    const [soundAssetsLoading, setSoundAssetsLoading] = useState<boolean>(true);
    const [soundUploading, setSoundUploading] = useState<boolean>(false);
    const [coverImagesLoading, setCoverImagesLoading] = useState<boolean>(false);
    const [coverImagePresets, setCoverImagePresets] = useState<WebGameImagePresetDto[]>([]);
    const [uploadedFolderImageUrls, setUploadedFolderImageUrls] = useState<string[]>([]);
    const [strapiCoverImages, setStrapiCoverImages] = useState<StrapiFolderImage[]>([]);
    const [strapiCoverListMessage, setStrapiCoverListMessage] = useState<string | null>(null);
    const [strapiSoundListMessage, setStrapiSoundListMessage] = useState<string | null>(null);
    const [userTagOptions, setUserTagOptions] = useState<UserTagDefinitionDto[]>([]);
    const [userTagsLoading, setUserTagsLoading] = useState<boolean>(false);
    const soundUploadInputRef = React.useRef<HTMLInputElement | null>(null);
    const latestPlayUserIdRef = React.useRef<number | null>(null);

    const gamesServerOrigin = useMemo(() => {
        const gameUrl = games.find(game => game.gameUrl)?.gameUrl;
        if (gameUrl) {
            try {
                return new URL(gameUrl).origin;
            } catch {
                // Fall through to env override.
            }
        }
        const envOrigin = process.env.REACT_APP_GAMES_SERVER_URL?.trim();
        if (!envOrigin) return '';
        try {
            return new URL(envOrigin).origin;
        } catch {
            return '';
        }
    }, [games]);

    const mergeSoundAssetPaths = (incoming: string[]) => {
        const normalized = incoming
            .map(path => normalizeSoundAssetStoredPath(path))
            .filter(Boolean);
        if (normalized.length === 0) return;
        setSoundAssets(prevState => Array.from(new Set([...prevState, ...normalized])).sort());
    };

    const mergeUploadedFolderImageUrls = (incoming: string[]) => {
        const normalized = incoming
            .map(path => normalizeCoverImageCollectionUrl(path))
            .filter(Boolean);
        if (normalized.length === 0) return;
        setUploadedFolderImageUrls(prevState => Array.from(new Set([...prevState, ...normalized])).sort());
    };

    const normalizeCoverImagePresets = (presets: WebGameImagePresetDto[]): WebGameImagePresetDto[] =>
        presets.map(preset => ({
            ...preset,
            url: normalizeCoverImageCollectionUrl(preset.url),
        }));

    const collectConfiguredUploadFolderImageUrls = (): string[] => {
        const fromConfigs = new Set<string>();
        games.forEach(game => {
            const cover = normalizeCoverImageCollectionUrl(game.coverImageUrl ?? '');
            if (isWebGameUploadsApiPath(cover)) {
                fromConfigs.add(cover);
            }
            slotSymbolImagesFromMetadata(game.metadata ?? {}).forEach(symbol => {
                const symbolPath = normalizeCoverImageCollectionUrl(symbol.imageUrl);
                if (isWebGameUploadsApiPath(symbolPath)) {
                    fromConfigs.add(symbolPath);
                }
            });
        });
        const currentCover = normalizeCoverImageCollectionUrl(editor.coverImageUrl);
        if (isWebGameUploadsApiPath(currentCover)) {
            fromConfigs.add(currentCover);
        }
        editor.slotSymbolImages.forEach(symbol => {
            const symbolPath = normalizeCoverImageCollectionUrl(symbol.imageUrl);
            if (isWebGameUploadsApiPath(symbolPath)) {
                fromConfigs.add(symbolPath);
            }
        });
        return Array.from(fromConfigs);
    };

    const refreshCoverImageLibrary = async () => {
        setCoverImagesLoading(true);
        try {
            const [presets, uploadedImages, strapiFolder] = await Promise.all([
                getWebGameImagePresets(),
                getWebGameUploadedImages(),
                listStrapiWebGameFolderImages(),
            ]);
            setCoverImagePresets(normalizeCoverImagePresets(presets ?? []));
            setUploadedFolderImageUrls(
                Array.from(new Set((uploadedImages ?? [])
                    .map(path => normalizeCoverImageCollectionUrl(path))
                    .filter(Boolean)))
                    .sort()
            );
            mergeUploadedFolderImageUrls(collectConfiguredUploadFolderImageUrls());
            setStrapiCoverImages(strapiFolder.images);
            setStrapiCoverListMessage(strapiFolder.message);
        } finally {
            setCoverImagesLoading(false);
        }
    };

    const soundAssetOptions = useMemo(() => {
        const options = new Set(soundAssets);
        [
            editor.backgroundMusicPath,
            ...Object.values(editor.sfxPaths)
        ].forEach(path => {
            const normalized = normalizeSoundAssetStoredPath(path);
            if (normalized) options.add(normalized);
        });
        return Array.from(options).sort();
    }, [soundAssets, editor.backgroundMusicPath, editor.sfxPaths]);

    const projectCoverImageUrls = useMemo(() => {
        const sources = games.map(game => ({
            coverImageUrl: game.coverImageUrl,
            metadata: game.metadata ?? {},
        }));
        const currentCover = editor.coverImageUrl.trim();
        if (currentCover) {
            sources.push({ coverImageUrl: currentCover, metadata: {} });
        }
        return collectWebGameProjectImageUrls(sources);
    }, [games, editor.coverImageUrl]);

    const selectedSoundProfile = useMemo(
        () => webGameSoundProfileForGame(selectedGame?.code, editor.htmlTemplateName),
        [selectedGame?.code, editor.htmlTemplateName]
    );

    const resolveSoundAssetUrl = (path: string): string => resolveWebGameSoundAssetUrl(path, { gamesServerOrigin });

    const refreshSoundAssets = async () => {
        setSoundAssetsLoading(true);
        try {
            const [response, strapiFolder] = await Promise.all([
                getWebGameSoundAssets(),
                listStrapiWebGameFolderAudio(),
            ]);
            mergeSoundAssetPaths((response?.items ?? []).map(item => item.path));
            mergeSoundAssetPaths(strapiFolder.audio.map(file => file.url));
            setStrapiSoundListMessage(strapiFolder.message);
        } finally {
            setSoundAssetsLoading(false);
        }
    };

    const loadGames = async () => {
        setLoading(true);
        try {
            const result = await getWebGameConfigs();
            setGames(result.data);
            if (result.error) {
                setMessage({ type: 'error', text: result.error });
            }
        } catch (error) {
            setGames([]);
            setMessage({
                type: 'error',
                text: error instanceof Error ? error.message : 'Failed to load web games.'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setUserTagsLoading(true);
            const tags = await getUserTags();
            if (cancelled) return;
            setUserTagOptions((tags ?? []).slice().sort((left, right) => left.tagName.localeCompare(right.tagName)));
            setUserTagsLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        loadGames();
    }, []);

    useEffect(() => {
        const fromConfigs = new Set<string>();
        games.forEach(game => {
            const metadata = game.metadata ?? {};
            const backgroundPath = stringFromMetadataObject(metadata, 'backgroundMusic', 'path').trim();
            if (backgroundPath) fromConfigs.add(backgroundPath);
            webGameConfigurableSfxMomentKeys().forEach(momentKey => {
                const path = sfxPathFromMetadata(metadata, momentKey).trim();
                if (path) fromConfigs.add(path);
            });
        });
        if (fromConfigs.size === 0) return;
        mergeSoundAssetPaths(Array.from(fromConfigs));
    }, [games]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setSoundAssetsLoading(true);
            try {
                const [response, strapiFolder] = await Promise.all([
                    getWebGameSoundAssets(),
                    listStrapiWebGameFolderAudio(),
                ]);
                if (cancelled) return;
                mergeSoundAssetPaths((response?.items ?? []).map(item => item.path));
                mergeSoundAssetPaths(strapiFolder.audio.map(file => file.url));
                setStrapiSoundListMessage(strapiFolder.message);
            } finally {
                if (!cancelled) {
                    setSoundAssetsLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        void refreshCoverImageLibrary();
    }, []);

    useEffect(() => {
        const fromConfigs = collectConfiguredUploadFolderImageUrls();
        if (fromConfigs.length === 0) return;
        mergeUploadedFolderImageUrls(fromConfigs);
    }, [games, editor.coverImageUrl, editor.slotSymbolImages]);

    const registerCoverImagePreset = async (url: string): Promise<boolean> => {
        const trimmedUrl = normalizeCoverImageStoredUrl(url);
        if (!trimmedUrl) return false;
        const preset = await createWebGameImagePreset({ url: trimmedUrl });
        if (!preset) return false;
        const normalizedPreset = {
            ...preset,
            url: normalizeCoverImageCollectionUrl(preset.url),
        };
        setCoverImagePresets(prev => [
            normalizedPreset,
            ...prev.filter(item => item.id !== normalizedPreset.id && item.url !== normalizedPreset.url),
        ]);
        return true;
    };

    const registerCurrentCoverImagePreset = async () => {
        const registered = await registerCoverImagePreset(editor.coverImageUrl);
        setMessage({
            type: registered ? 'success' : 'error',
            text: registered ? coverCopy.addCurrentSuccess : coverCopy.addCurrentFailed,
        });
    };

    const deleteCoverImagePreset = async (preset: WebGameImagePresetDto) => {
        const deleted = await deleteWebGameImagePreset(preset.id);
        if (!deleted) {
            setMessage({ type: 'error', text: coverCopy.deleteFailed });
            return;
        }
        setCoverImagePresets(prev => prev.filter(item => item.id !== preset.id));
    };

    const updateSlotSymbolImageUrl = (symbolId: string, imageUrl: string) => {
        setEditor(prevState => ({
            ...prevState,
            slotSymbolImages: prevState.slotSymbolImages.map(symbol => (
                symbol.id === symbolId ? { ...symbol, imageUrl } : symbol
            ))
        }));
    };

    const handleSoundUpload = async (file: File | null | undefined) => {
        if (!file) return;
        setSoundUploading(true);
        const uploadedPath = await uploadFile(file, DestinationFolder.WebGames, editor.soundUploadPrefix);
        setSoundUploading(false);
        if (uploadedPath == null) {
            setMessage({ type: 'error', text: soundCopy.uploadFailed });
            return;
        }
        mergeSoundAssetPaths([uploadedPath]);
        setMessage({ type: 'success', text: soundCopy.uploadSuccess });
    };

    const openEditor = (game: WebGameAdminConfigDto) => {
        setSelectedGame(game);
        setEditor(toEditorState(game));
        setMessage(null);
    };

    const closeEditor = () => {
        setSelectedGame(null);
        setEditor(emptyEditor);
        setSaving(false);
    };

    const updateEditor = <K extends keyof EditorState>(key: K, value: EditorState[K]) => {
        setEditor(prevState => ({ ...prevState, [key]: value }));
    };

    const updateSegmentRule = <K extends keyof SegmentWinRuleEditor>(
        index: number,
        key: K,
        value: SegmentWinRuleEditor[K]
    ) => {
        setEditor(prevState => ({
            ...prevState,
            segmentRules: prevState.segmentRules.map((rule, ruleIndex) => (
                ruleIndex === index ? { ...rule, [key]: value } : rule
            ))
        }));
    };

    const addSegmentRule = () => {
        setEditor(prevState => ({
            ...prevState,
            segmentRules: [...prevState.segmentRules, emptySegmentWinRule()]
        }));
    };

    const removeSegmentRule = (index: number) => {
        setEditor(prevState => ({
            ...prevState,
            segmentRules: prevState.segmentRules.filter((_, ruleIndex) => ruleIndex !== index)
        }));
    };

    const updateSfxPath = (momentKey: string, value: string) => {
        setEditor(prevState => ({
            ...prevState,
            sfxPaths: {
                ...prevState.sfxPaths,
                [momentKey]: value
            }
        }));
    };

    const fetchPlayUserInfo = async (userId: number) => {
        latestPlayUserIdRef.current = userId;
        setPlayUserLoading(true);
        setPlayError(null);
        const result = await getUserAggregatedInfo(userId);
        if (latestPlayUserIdRef.current !== userId) return;
        setPlayUserInfo(result);
        setPlayUserLoading(false);
        if (result == null) {
            setPlayError('User was not found.');
        }
    };

    const openPlayDialog = (game: WebGameAdminConfigDto) => {
        const storedUserId = window.localStorage.getItem(PLAY_AS_USER_STORAGE_KEY) ?? '';
        setPlayGame(game);
        setPlayUserIdText(storedUserId);
        setPlayUserInfo(null);
        setPlayError(null);
        setUserSearchTerm('');
        setUserOptions([]);

        const numericUserId = Number(storedUserId);
        if (Number.isFinite(numericUserId) && numericUserId > 0) {
            fetchPlayUserInfo(numericUserId);
        }
    };

    const closePlayDialog = () => {
        setPlayGame(null);
        setPlayUserInfo(null);
        setPlayUserIdText('');
        setPlayError(null);
        setPlayLaunching(false);
        latestPlayUserIdRef.current = null;
        setUserSearchTerm('');
        setUserOptions([]);
    };

    const onPlayUserIdChanged = (value: string) => {
        setPlayUserIdText(value);
        setPlayUserInfo(null);
        setPlayError(null);

        const numericUserId = Number(value);
        if (Number.isFinite(numericUserId) && numericUserId > 0) {
            fetchPlayUserInfo(numericUserId);
        }
    };

    const launchGame = async () => {
        if (!playGame) return;
        const userId = Number(playUserIdText);
        if (!Number.isFinite(userId) || userId <= 0) {
            setPlayError('Enter a valid user id.');
            return;
        }
        if (playUserInfo == null) {
            setPlayError('Load a valid user profile before launching the game.');
            return;
        }

        setPlayLaunching(true);
        const sessionCode = await generateUserWebGameSessionCode(userId);
        if (sessionCode == null) {
            setPlayLaunching(false);
            setPlayError('Failed to create game session code.');
            return;
        }

        window.localStorage.setItem(PLAY_AS_USER_STORAGE_KEY, String(userId));
        const gameUrl = new URL(playGame.gameUrl);
        gameUrl.searchParams.set('gameSessionCode', sessionCode.gameSessionCode);
        gameUrl.searchParams.set('platform', 'web');
        gameUrl.searchParams.set('bridgeOrigin', window.location.origin);

        const previewUrl = `/web-games/preview?url=${encodeURIComponent(gameUrl.toString())}&title=${encodeURIComponent(playGame.name)}&userId=${encodeURIComponent(String(userId))}&gameCode=${encodeURIComponent(playGame.code)}`;
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
        closePlayDialog();
    };

    useEffect(() => {
        if (playGame == null || userSearchTerm.trim().length < 2) {
            setUserOptions([]);
            return;
        }

        const timeoutId = window.setTimeout(async () => {
            setUserOptionsLoading(true);
            const result = await getUsersPaginated(0, 10, userSearchTerm.trim());
            setUserOptions(result?.items ?? []);
            setUserOptionsLoading(false);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [playGame, userSearchTerm]);

    const saveGame = async () => {
        if (!selectedGame) return;

        let payload: UpdateWebGameAdminConfigRequest;
        try {
            const metadata = parseJsonObject(editor.metadataText, 'Metadata');
            const isAviatorEdit = selectedGame.code === AVIATOR_SIMPLIFIED_GAME_CODE;
            const playerWinChance = parseNumberInput(editor.playerWinChance, 'Player win chance');
            const engagementMaxBoost = parseNumberInput(editor.engagementMaxBoost, 'Engagement max boost');
            const engagementLossStreakStep = parseNumberInput(editor.engagementLossStreakStep, 'Engagement loss streak step');
            const engagementMinPlayerWinChance = parseNumberInput(editor.engagementMinPlayerWinChance, 'Engagement minimum player win chance');
            const engagementMaxPlayerWinChance = parseNumberInput(editor.engagementMaxPlayerWinChance, 'Engagement maximum player win chance');
            const backgroundMusicVolume = parseNumberInput(editor.backgroundMusicVolume, 'Background music volume');
            const backgroundMusicPlaybackRate = parseNumberInput(editor.backgroundMusicPlaybackRate, 'Background music playback rate');

            if (playerWinChance < 0 || playerWinChance > 1) {
                throw new Error('Player win chance must be between 0 and 1.');
            }
            if (engagementMaxBoost < 0 || engagementMaxBoost > 1) {
                throw new Error('Engagement max boost must be between 0 and 1.');
            }
            if (!Number.isInteger(engagementLossStreakStep) || engagementLossStreakStep < 1) {
                throw new Error('Engagement loss streak step must be an integer greater than or equal to 1.');
            }
            if (engagementMinPlayerWinChance < 0 || engagementMinPlayerWinChance > 1) {
                throw new Error('Engagement minimum player win chance must be between 0 and 1.');
            }
            if (engagementMaxPlayerWinChance < 0 || engagementMaxPlayerWinChance > 1) {
                throw new Error('Engagement maximum player win chance must be between 0 and 1.');
            }
            if (engagementMinPlayerWinChance > engagementMaxPlayerWinChance) {
                throw new Error('Engagement minimum player win chance cannot be greater than engagement maximum player win chance.');
            }
            if (backgroundMusicVolume < 0 || backgroundMusicVolume > 1) {
                throw new Error('Background music volume must be between 0 and 1.');
            }
            if (backgroundMusicPlaybackRate < 0.5 || backgroundMusicPlaybackRate > 2) {
                throw new Error('Background music playback rate must be between 0.5 and 2.');
            }
            const segmentWinRules = isAviatorEdit
                ? []
                : editor.segmentRules
                .map(rule => {
                    const tagNames = rule.tagNames
                        .map(tag => tag.trim().toLowerCase())
                        .filter(tag => tag.length > 0)
                        .slice(0, 1);
                    if (tagNames.length === 0) {
                        return null;
                    }
                    const segmentWelcomeFavorableWinsTarget = parseNumberInput(
                        rule.welcomeFavorableWinsTarget.trim(),
                        'Segment generosity (favorable wins target)'
                    );
                    const segmentWelcomeMaxRoundsInWindow = parseNumberInput(
                        rule.welcomeMaxRoundsInWindow.trim(),
                        'Segment sessions (max generous rounds)'
                    );
                    if (!Number.isInteger(segmentWelcomeFavorableWinsTarget) || segmentWelcomeFavorableWinsTarget < 1) {
                        throw new Error('Segment generosity must be an integer >= 1 for each configured user tag.');
                    }
                    if (!Number.isInteger(segmentWelcomeMaxRoundsInWindow) || segmentWelcomeMaxRoundsInWindow < 1) {
                        throw new Error('Segment sessions must be an integer >= 1 for each configured user tag.');
                    }
                    return {
                        tagNames,
                        welcomeWindow: {
                            favorableWinsTarget: segmentWelcomeFavorableWinsTarget,
                            maxRoundsInWindow: segmentWelcomeMaxRoundsInWindow
                        }
                    };
                })
                .filter(rule => rule != null);
            const backgroundMusicPath = normalizeSoundAssetStoredPath(editor.backgroundMusicPath);
            const existingSfxCatalog = metadata.sfxCatalog;
            const normalizedSfxPaths = Object.fromEntries(
                Object.entries(editor.sfxPaths).map(([momentKey, path]) => [
                    momentKey,
                    normalizeSoundAssetStoredPath(path)
                ])
            );
            const sfxCatalog = mergeWebGameSfxCatalogFromEditor(
                existingSfxCatalog != null && typeof existingSfxCatalog === 'object' && !Array.isArray(existingSfxCatalog)
                    ? existingSfxCatalog as Record<string, unknown>
                    : undefined,
                normalizedSfxPaths
            );
            const mergedMetadata: Record<string, unknown> = {
                ...metadata,
                sfxCatalog,
                seeded: false
            };
            if (!isAviatorEdit) {
                mergedMetadata.playerWinChance = playerWinChance;
                mergedMetadata.engagementTuningEnabled = editor.engagementTuningEnabled;
                mergedMetadata.engagementMaxBoost = engagementMaxBoost;
                mergedMetadata.engagementLossStreakStep = engagementLossStreakStep;
                mergedMetadata.engagementMinPlayerWinChance = engagementMinPlayerWinChance;
                mergedMetadata.engagementMaxPlayerWinChance = engagementMaxPlayerWinChance;
                mergedMetadata.openingRules = [];
                mergedMetadata.segmentWinRules = segmentWinRules;
                delete mergedMetadata.welcomeWindow;
            }
            if (backgroundMusicPath) {
                mergedMetadata.backgroundMusic = {
                    path: backgroundMusicPath,
                    volume: backgroundMusicVolume,
                    playbackRate: backgroundMusicPlaybackRate
                };
            } else {
                delete mergedMetadata.backgroundMusic;
            }
            if (selectedGame.code === SLOT_MACHINE_GAME_CODE) {
                Object.assign(
                    mergedMetadata,
                    mergeSlotSymbolImagesIntoMetadata(mergedMetadata, editor.slotSymbolImages)
                );
            }

            payload = {
                name: editor.name.trim(),
                description: editor.description.trim() || null,
                enabled: editor.enabled,
                order: Number(editor.order),
                coverImageUrl: normalizeCoverImageStoredUrl(editor.coverImageUrl) || null,
                htmlTemplateName: editor.htmlTemplateName.trim(),
                localizations: parseJsonObject(editor.localizationsText, 'Localizations') as Record<string, string>,
                descriptionLocalizations: parseJsonObject(editor.descriptionLocalizationsText, 'Description localizations') as Record<string, string>,
                metadata: mergedMetadata
            };
        } catch (error) {
            setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Invalid JSON configuration.' });
            return;
        }

        if (!payload.name || !payload.htmlTemplateName || !Number.isFinite(payload.order)) {
            setMessage({ type: 'error', text: 'Name, order, and HTML template are required.' });
            return;
        }

        setSaving(true);
        const updated = await updateWebGameConfig(selectedGame.code, payload);
        setSaving(false);

        if (updated == null) {
            setMessage({ type: 'error', text: 'Failed to update web mini game configuration.' });
            return;
        }

        setGames(prevState => prevState.map(game => game.code === updated.code ? updated : game));
        setMessage({ type: 'success', text: 'Web mini game configuration updated.' });
        closeEditor();
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1">Web Mini Games</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Catalog and dashboard-editable configuration for browser-tested mini games.
                    </Typography>
                    <Typography variant="body2" color="primary.main" sx={{ mt: 1, maxWidth: 720 }}>
                        {egCopy.catalogEngagementHint}
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, maxWidth: 900, color: 'text.secondary' }}>
                        {egCopy.catalogEngagementBullets.map(item => (
                            <Typography key={item} component="li" variant="body2" sx={{ mb: 0.5 }}>
                                {item}
                            </Typography>
                        ))}
                    </Box>
                </Box>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadGames} disabled={loading}>
                    Refresh
                </Button>
            </Stack>

            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : games.length === 0 ? (
                <Typography color="text.secondary">No web mini games are configured.</Typography>
            ) : (
                <Grid container spacing={3}>
                    {games.map(game => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={game.code}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '8px' }}>
                                <CardMedia
                                    component="img"
                                    image={resolveWebGameCoverImageUrl(game.resolvedCoverImageUrl || game.coverImageUrl || '', { gamesServerOrigin })}
                                    alt={game.name}
                                    sx={{
                                        height: 170,
                                        objectFit: 'cover',
                                        bgcolor: '#eef2f7',
                                        borderBottom: theme => `1px solid ${theme.palette.divider}`
                                    }}
                                />
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                            <Box>
                                                <Typography variant="h6" component="h2">{game.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">{game.code}</Typography>
                                            </Box>
                                            <Chip
                                                label={game.enabled ? 'Enabled' : 'Disabled'}
                                                color={game.enabled ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60 }}>
                                            {game.description || 'No description.'}
                                        </Typography>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip size="small" label={`Order ${game.order}`} />
                                            <Chip size="small" label={`Template ${game.htmlTemplateName}`} />
                                            <Chip size="small" label={`Bet ${numberFromMetadata(game.metadata, 'minBetAmount')} - ${numberFromMetadata(game.metadata, 'maxBetAmount')}`} />
                                            <Chip
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                label={egCopy.cardWinChanceChip.replace('{value}', formatCardWinChance(game))}
                                            />
                                            <Chip
                                                size="small"
                                                color={booleanFromUnknown(game.metadata?.engagementTuningEnabled, DEFAULT_ENGAGEMENT_TUNING_ENABLED) ? 'success' : 'default'}
                                                variant="outlined"
                                                label={
                                                    booleanFromUnknown(game.metadata?.engagementTuningEnabled, DEFAULT_ENGAGEMENT_TUNING_ENABLED)
                                                        ? egCopy.cardEngagementOnChip
                                                        : egCopy.cardEngagementOffChip
                                                }
                                            />
                                            <Chip
                                                size="small"
                                                color={hasConfiguredWelcomeWindow(game.metadata) ? 'info' : 'default'}
                                                variant="outlined"
                                                label={
                                                    hasConfiguredWelcomeWindow(game.metadata)
                                                        ? egCopy.cardOpeningConfiguredChip
                                                        : egCopy.cardOpeningDefaultChip
                                                }
                                            />
                                        </Stack>
                                    </Stack>
                                </CardContent>
                                <CardActions sx={{ px: 2, pb: 2 }}>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                                        <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => openPlayDialog(game)} fullWidth>
                                            Play
                                        </Button>
                                        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => openEditor(game)} fullWidth>
                                            Edit
                                        </Button>
                                    </Stack>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={playGame != null} onClose={closePlayDialog} fullWidth maxWidth="sm">
                <DialogTitle>Play Web Mini Game</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {playGame && (
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    component="img"
                                    src={playGame.resolvedCoverImageUrl || playGame.coverImageUrl || ''}
                                    alt={playGame.name}
                                    sx={{ width: 128, height: 82, objectFit: 'cover', borderRadius: '8px', bgcolor: '#eef2f7' }}
                                />
                                <Box>
                                    <Typography variant="h6">{playGame.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {playGame.description || 'No description.'}
                                    </Typography>
                                </Box>
                            </Stack>
                        )}
                        <TextField
                            label="User id"
                            type="number"
                            value={playUserIdText}
                            onChange={event => onPlayUserIdChanged(event.target.value)}
                            fullWidth
                        />
                        <Autocomplete
                            options={userOptions}
                            loading={userOptionsLoading}
                            inputValue={userSearchTerm}
                            onInputChange={(_, value) => setUserSearchTerm(value)}
                            getOptionLabel={(option) => `${option.name || option.email || 'User'} (${option.id})`}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(_, value) => {
                                if (value != null) {
                                    onPlayUserIdChanged(String(value.id));
                                }
                            }}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar src={option.photoUrl} sx={{ width: 32, height: 32 }} />
                                        <Box>
                                            <Typography variant="body2">{option.name || option.email || `User ${option.id}`}</Typography>
                                            <Typography variant="caption" color="text.secondary">{option.email} · ID {option.id}</Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search user"
                                    placeholder="Type name or email"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {userOptionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                        {playUserLoading ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">Loading user profile...</Typography>
                            </Stack>
                        ) : playUserInfo ? (
                            <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar src={playUserInfo.general.photoUrl} sx={{ width: 44, height: 44 }} />
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>{playUserInfo.general.name || playUserInfo.general.email}</Typography>
                                        <Typography variant="body2" color="text.secondary">ID {playUserInfo.general.id} · {playUserInfo.general.email}</Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" color="text.secondary">Balance</Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>{playUserInfo.balance.moneyInfo}</Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        ) : null}
                        {playError && <Alert severity="error">{playError}</Alert>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePlayDialog} disabled={playLaunching}>Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={playLaunching ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                        disabled={playLaunching || playUserLoading || playUserInfo == null}
                        onClick={launchGame}
                    >
                        Open game
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={selectedGame != null} onClose={closeEditor} fullWidth maxWidth="md">
                <DialogTitle>Edit Web Mini Game</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {selectedGame != null && (
                            <Alert severity="info" icon={false} sx={{ border: 1, borderColor: 'info.main' }}>
                                <Typography variant="subtitle2" component="p" fontWeight={700} gutterBottom>
                                    {selectedGame.name}{' '}
                                    <Typography component="span" variant="caption" color="text.secondary">
                                        ({selectedGame.code})
                                    </Typography>
                                </Typography>
                                <Typography variant="body2">
                                    {egCopy.editPerGameAlert.replace(/\{gameCode\}/g, selectedGame.code)}
                                </Typography>
                            </Alert>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Name"
                                value={editor.name}
                                onChange={event => updateEditor('name', event.target.value)}
                                fullWidth
                            />
                            <TextField
                                label="Order"
                                type="number"
                                value={editor.order}
                                onChange={event => updateEditor('order', Number(event.target.value))}
                                sx={{ width: { xs: '100%', sm: 160 } }}
                            />
                        </Stack>
                        <TextField
                            label="Description"
                            value={editor.description}
                            onChange={event => updateEditor('description', event.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <TextField
                                label={coverCopy.label}
                                value={editor.coverImageUrl}
                                onChange={event => updateEditor('coverImageUrl', event.target.value)}
                                helperText={coverCopy.helper}
                                fullWidth
                            />
                            <WebGameCoverImagePresetPicker
                                value={editor.coverImageUrl}
                                presets={coverImagePresets}
                                projectImageUrls={projectCoverImageUrls}
                                uploadedFolderImageUrls={uploadedFolderImageUrls}
                                strapiImages={strapiCoverImages}
                                gamesServerOrigin={gamesServerOrigin}
                                copy={coverCopy}
                                onSelect={url => updateEditor('coverImageUrl', normalizeCoverImageStoredUrl(url))}
                                onDelete={deleteCoverImagePreset}
                            />
                            <FormControlLabel
                                control={<Switch checked={editor.enabled} onChange={event => updateEditor('enabled', event.target.checked)} />}
                                label="Enabled"
                                sx={{ minWidth: 120 }}
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                            <Button
                                variant="text"
                                onClick={() => void refreshCoverImageLibrary()}
                                disabled={coverImagesLoading}
                            >
                                {coverCopy.refreshCatalog}
                            </Button>
                            <Button
                                variant="text"
                                disabled={!editor.coverImageUrl.trim()}
                                onClick={() => void registerCurrentCoverImagePreset()}
                            >
                                {coverCopy.addCurrentToCollection}
                            </Button>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">{coverCopy.pickerHelper}</Typography>
                        {strapiCoverListMessage ? (
                            <Typography variant="body2" color="text.secondary">
                                {strapiCoverListMessage} {coverCopy.strapiFolderNote}
                            </Typography>
                        ) : null}
                        <CoverImagePreview
                            label={coverCopy.previewLabel}
                            source={resolveWebGameCoverImageUrl(editor.coverImageUrl, { gamesServerOrigin })}
                            unavailableText={coverCopy.previewUnavailable}
                        />
                        {selectedGame?.code === SLOT_MACHINE_GAME_CODE ? (
                            <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 2 }}>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h6" component="h3" fontWeight={800} color="primary.main" sx={{ letterSpacing: 0.2 }}>
                                            {slotSymbolCopy.sectionTitle}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                            {slotSymbolCopy.sectionIntro}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {slotSymbolCopy.uploadsFolderHelper}
                                        </Typography>
                                    </Box>
                                    {editor.slotSymbolImages.map(symbol => (
                                        <Box
                                            key={`slot-symbol-${symbol.id}`}
                                            sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}
                                        >
                                            <Stack spacing={1.5}>
                                                <Typography variant="subtitle2" fontWeight={700}>
                                                    {symbol.label}{' '}
                                                    <Typography component="span" variant="caption" color="text.secondary">
                                                        ({symbol.id})
                                                    </Typography>
                                                </Typography>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                                    <TextField
                                                        label={slotSymbolCopy.imageUrlLabel}
                                                        value={symbol.imageUrl}
                                                        onChange={event => updateSlotSymbolImageUrl(symbol.id, event.target.value)}
                                                        helperText={slotSymbolCopy.imageUrlHelper}
                                                        fullWidth
                                                    />
                                                    <WebGameCoverImagePresetPicker
                                                        value={symbol.imageUrl}
                                                        presets={coverImagePresets}
                                                        projectImageUrls={projectCoverImageUrls}
                                                        uploadedFolderImageUrls={uploadedFolderImageUrls}
                                                        strapiImages={strapiCoverImages}
                                                        gamesServerOrigin={gamesServerOrigin}
                                                        copy={coverCopy}
                                                        onSelect={url => updateSlotSymbolImageUrl(symbol.id, url)}
                                                        onDelete={deleteCoverImagePreset}
                                                    />
                                                </Stack>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {slotSymbolCopy.defaultPathLabel}: {symbol.defaultImageUrl}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {slotSymbolCopy.uploadsPathLabel}: {slotMachineSymbolUploadStoredPath(symbol.id)}
                                                    </Typography>
                                                </Stack>
                                                <CoverImagePreview
                                                    label={slotSymbolCopy.previewLabel}
                                                    source={resolveWebGameCoverImageUrl(
                                                        symbol.imageUrl.trim() || symbol.defaultImageUrl,
                                                        { gamesServerOrigin }
                                                    )}
                                                    unavailableText={slotSymbolCopy.previewUnavailable}
                                                />
                                            </Stack>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        ) : null}
                        <TextField
                            label="HTML template"
                            value={editor.htmlTemplateName}
                            onChange={event => updateEditor('htmlTemplateName', event.target.value)}
                            fullWidth
                        />
                        <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 2 }}>
                            <Stack spacing={2}>
                                {selectedGame?.code === AVIATOR_SIMPLIFIED_GAME_CODE ? (
                                    <Alert severity="warning" variant="outlined" sx={{ py: 1 }}>
                                        <Typography variant="body2">{egCopy.aviatorWarning}</Typography>
                                    </Alert>
                                ) : (
                                    <>
                                        <Box>
                                            <Typography variant="h6" component="h3" fontWeight={800} color="primary.main" sx={{ letterSpacing: 0.2 }}>
                                                {egCopy.sectionTitle}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                                {egCopy.sectionIntro}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                {egCopy.twoLayersIntro}
                                            </Typography>
                                        </Box>
                                        <Alert severity="info" variant="outlined" sx={{ py: 1 }}>
                                            <Typography variant="body2">
                                                {egCopy.storageNote.replace(
                                                    '{gameCode}',
                                                    selectedGame != null ? selectedGame.code : '{gameCode}'
                                                )}
                                            </Typography>
                                        </Alert>
                                        <Alert severity="info" variant="outlined" sx={{ py: 1 }}>
                                            <Typography variant="body2">{egCopy.defaultsNote}</Typography>
                                        </Alert>
                                        <Alert severity="warning" variant="outlined" sx={{ py: 1 }}>
                                            <Typography variant="body2">{egCopy.userTagsNote}</Typography>
                                        </Alert>
                                <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {egCopy.segmentRulesTitle}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {egCopy.segmentRulesIntro}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Button variant="outlined" size="small" onClick={addSegmentRule}>
                                                {uiLang === 'ro' ? 'Adaugă user tag' : 'Add user tag'}
                                            </Button>
                                            {userTagsLoading ? <CircularProgress size={16} /> : null}
                                        </Stack>
                                        {editor.segmentRules.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                {uiLang === 'ro' ? 'Niciun user tag configurat.' : 'No user tags configured.'}
                                            </Typography>
                                        ) : editor.segmentRules.map((rule, index) => (
                                            <Box
                                                key={`segment-rule-${index}`}
                                                sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}
                                            >
                                                <Stack spacing={1.5}>
                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                        <Typography variant="subtitle2" fontWeight={700}>
                                                            {uiLang === 'ro' ? `User tag ${index + 1}` : `User tag ${index + 1}`}
                                                        </Typography>
                                                        <Button color="error" size="small" onClick={() => removeSegmentRule(index)}>
                                                            {uiLang === 'ro' ? 'Șterge' : 'Remove'}
                                                        </Button>
                                                    </Stack>
                                                    <Autocomplete
                                                        options={userTagOptions.map(tag => tag.tagName)}
                                                        loading={userTagsLoading}
                                                        value={rule.tagNames[0] ?? null}
                                                        onChange={(_, value) => updateSegmentRule(index, 'tagNames', value ? [value] : [])}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                label={egCopy.segmentRuleTagNamesLabel}
                                                                helperText={egCopy.segmentRuleTagNamesHelper}
                                                            />
                                                        )}
                                                    />
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                        <TextField
                                                            label={egCopy.segmentRuleWelcomeFavorableWinsLabel}
                                                            type="number"
                                                            value={rule.welcomeFavorableWinsTarget}
                                                            onChange={event => updateSegmentRule(index, 'welcomeFavorableWinsTarget', event.target.value)}
                                                            inputProps={{ min: 1, step: 1 }}
                                                            fullWidth
                                                        />
                                                        <TextField
                                                            label={egCopy.segmentRuleWelcomeMaxRoundsLabel}
                                                            type="number"
                                                            value={rule.welcomeMaxRoundsInWindow}
                                                            onChange={event => updateSegmentRule(index, 'welcomeMaxRoundsInWindow', event.target.value)}
                                                            inputProps={{ min: 1, step: 1 }}
                                                            fullWidth
                                                        />
                                                    </Stack>
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                                <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {egCopy.defaultWinChanceTitle}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {egCopy.defaultWinChanceIntro}
                                            </Typography>
                                        </Box>
                                        <TextField
                                            label={egCopy.playerWinChanceLabel}
                                            type="number"
                                            value={editor.playerWinChance}
                                            onChange={event => updateEditor('playerWinChance', event.target.value)}
                                            inputProps={{ min: 0, max: 1, step: 0.01 }}
                                            helperText={egCopy.playerWinChanceHelper}
                                            fullWidth
                                        />
                                    </Stack>
                                </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>
                        <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 2 }}>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="h6" component="h3" fontWeight={800} color="primary.main" sx={{ letterSpacing: 0.2 }}>
                                        {soundCopy.sectionTitle}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                        {soundCopy.sectionIntro}
                                    </Typography>
                                </Box>
                                <Alert severity="info" variant="outlined" sx={{ py: 1 }}>
                                    <Typography variant="body2">{soundCopy.storageNote}</Typography>
                                </Alert>
                                <Alert severity="info" variant="outlined" sx={{ py: 1 }}>
                                    <Typography variant="body2">{webGameSoundSupportSummary(uiLang)}</Typography>
                                </Alert>
                                {selectedSoundProfile != null && (
                                    <Alert severity={selectedSoundProfile.supportsMetadataSounds ? 'success' : 'warning'} variant="outlined" sx={{ py: 1 }}>
                                        <Typography variant="body2">{selectedSoundProfile.notes[uiLang]}</Typography>
                                    </Alert>
                                )}
                                {selectedSoundProfile != null && (
                                    <WebGameSoundMomentsInventory
                                        moments={selectedSoundProfile.moments}
                                        lang={uiLang}
                                        copy={soundCopy}
                                    />
                                )}
                                {soundAssetsLoading ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <CircularProgress size={18} />
                                        <Typography variant="body2" color="text.secondary">{soundCopy.soundAssetsLoading}</Typography>
                                    </Stack>
                                ) : soundAssets.length === 0 ? (
                                    <Alert severity="warning" variant="outlined" sx={{ py: 1 }}>
                                        <Typography variant="body2">{soundCopy.soundAssetsEmpty}</Typography>
                                    </Alert>
                                ) : null}
                                <TextField
                                    label={uiLang === 'ro' ? 'Prefix upload audio' : 'Audio upload prefix'}
                                    value={editor.soundUploadPrefix}
                                    onChange={event => updateEditor('soundUploadPrefix', event.target.value)}
                                    helperText={uiLang === 'ro'
                                        ? 'Opțional: prefix pentru fișiere în uploads_api/webgames.'
                                        : 'Optional prefix for uploads_api/webgames files.'}
                                    fullWidth
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        disabled={soundUploading}
                                        startIcon={soundUploading ? <CircularProgress size={16} /> : undefined}
                                    >
                                        {soundCopy.uploadButton}
                                        <input
                                            ref={soundUploadInputRef}
                                            type="file"
                                            hidden
                                            accept="audio/mpeg,audio/mp3,audio/ogg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.mp3,.ogg,.wav,.m4a"
                                            onChange={event => {
                                                const file = event.target.files?.[0];
                                                void handleSoundUpload(file);
                                                event.target.value = '';
                                            }}
                                        />
                                    </Button>
                                    <Button variant="text" onClick={() => refreshSoundAssets()} disabled={soundAssetsLoading || soundUploading}>
                                        {soundCopy.refreshCatalog}
                                    </Button>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">{soundCopy.uploadHelper}</Typography>
                                {strapiSoundListMessage ? (
                                    <Typography variant="body2" color="text.secondary">
                                        {strapiSoundListMessage} {soundCopy.strapiFolderNote}
                                    </Typography>
                                ) : null}
                                <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {soundCopy.backgroundMusicTitle}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {soundCopy.backgroundMusicIntro}
                                            </Typography>
                                        </Box>
                                        <Autocomplete
                                            freeSolo
                                            options={soundAssetOptions}
                                            value={editor.backgroundMusicPath}
                                            onChange={(_, value) => updateEditor('backgroundMusicPath', value ?? '')}
                                            onInputChange={(_, value) => updateEditor('backgroundMusicPath', value)}
                                            renderInput={params => (
                                                <TextField
                                                    {...params}
                                                    label={soundCopy.backgroundMusicPathLabel}
                                                    placeholder={soundCopy.backgroundMusicPathPlaceholder}
                                                    helperText={soundMomentPickerHelper(
                                                        selectedSoundProfile?.moments.find(moment => moment.key === 'background'),
                                                        soundCopy.backgroundMusicPathHelper,
                                                        uiLang,
                                                        soundCopy.momentPickerDurationPrefix
                                                    )}
                                                />
                                            )}
                                        />
                                        <SoundPreview
                                            label={soundCopy.previewLabel}
                                            source={resolveSoundAssetUrl(editor.backgroundMusicPath)}
                                            unavailableText={soundCopy.previewUnavailable}
                                            openLinkLabel={soundCopy.previewOpenLinkLabel}
                                        />
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                            <TextField
                                                label={soundCopy.backgroundMusicVolumeLabel}
                                                type="number"
                                                value={editor.backgroundMusicVolume}
                                                onChange={event => updateEditor('backgroundMusicVolume', event.target.value)}
                                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                                                helperText={soundCopy.backgroundMusicVolumeHelper}
                                                fullWidth
                                            />
                                            <TextField
                                                label={soundCopy.backgroundMusicPlaybackRateLabel}
                                                type="number"
                                                value={editor.backgroundMusicPlaybackRate}
                                                onChange={event => updateEditor('backgroundMusicPlaybackRate', event.target.value)}
                                                inputProps={{ min: 0.5, max: 2, step: 0.05 }}
                                                helperText={soundCopy.backgroundMusicPlaybackRateHelper}
                                                fullWidth
                                            />
                                        </Stack>
                                    </Stack>
                                </Box>
                                <Box sx={{ border: theme => `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 1.5 }}>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {soundCopy.sfxTitle}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {soundCopy.sfxIntro}
                                            </Typography>
                                        </Box>
                                        {(selectedSoundProfile?.moments ?? [])
                                            .filter(moment => moment.configurableInDashboard && moment.binding === 'sfxCatalog')
                                            .map(moment => (
                                                <Box key={`sfx-${moment.key}`}>
                                                    <Autocomplete
                                                        freeSolo
                                                        options={soundAssetOptions}
                                                        value={editor.sfxPaths[moment.key] ?? ''}
                                                        onChange={(_, value) => updateSfxPath(moment.key, value ?? '')}
                                                        onInputChange={(_, value) => updateSfxPath(moment.key, value)}
                                                        renderInput={params => (
                                                            <TextField
                                                                {...params}
                                                                label={`${moment.labels[uiLang]} (${moment.key})`}
                                                                placeholder={soundCopy.sfxPathPlaceholder}
                                                                helperText={soundMomentPickerHelper(
                                                                    moment,
                                                                    soundCopy.sfxMomentHelper,
                                                                    uiLang,
                                                                    soundCopy.momentPickerDurationPrefix
                                                                )}
                                                            />
                                                        )}
                                                    />
                                                    <SoundPreview
                                                        label={soundCopy.previewLabel}
                                                        source={resolveSoundAssetUrl(editor.sfxPaths[moment.key] ?? '')}
                                                        unavailableText={soundCopy.previewUnavailable}
                                                        openLinkLabel={soundCopy.previewOpenLinkLabel}
                                                    />
                                                </Box>
                                            ))}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                        <TextField
                            label={egCopy.advancedMetadataTitle}
                            value={editor.metadataText}
                            onChange={event => updateEditor('metadataText', event.target.value)}
                            multiline
                            rows={9}
                            fullWidth
                            helperText={egCopy.metadataMergeNote}
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                        <TextField
                            label="Name localizations JSON"
                            value={editor.localizationsText}
                            onChange={event => updateEditor('localizationsText', event.target.value)}
                            multiline
                            rows={4}
                            fullWidth
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                        <TextField
                            label="Description localizations JSON"
                            value={editor.descriptionLocalizationsText}
                            onChange={event => updateEditor('descriptionLocalizationsText', event.target.value)}
                            multiline
                            rows={4}
                            fullWidth
                            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={saveGame} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default WebGamesPage;
